import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import {
  RiArrowRightLine, RiCalendarEventLine, RiMapPinLine, RiTimeLine,
  RiUserStarLine, RiGlobalLine, RiGroupLine, RiMedalLine,
  RiFolderChartLine, RiLightbulbLine, RiComputerLine, RiTeamLine,
  RiHeartLine, RiArrowUpLine, RiSearchLine, RiRefreshLine, RiCloseLine
} from 'react-icons/ri';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import { API_BASE } from '../config';

/* ─── THEME COLORS (Teal & Gold Palette) ───────────────────────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradAccent: "linear-gradient(135deg, #3BBCAE 0%, #2A9D8F 100%)",
  success: "#10B981",
  warning: "#D4A017",
  danger: "#EF4444",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  
  ::-webkit-scrollbar{width:8px}
  ::-webkit-scrollbar-track{background:${T.borderLight}}
  ::-webkit-scrollbar-thumb{background:${T.primaryLight};border-radius:4px}

  .t-hero{background:${T.gradSoft};border-bottom:1px solid ${T.border};padding:clamp(40px,7vw,80px) clamp(16px,4vw,64px) clamp(32px,5vw,64px);position:relative;overflow:hidden}
  .t-hero::before{content:'';position:absolute;top:-120px;left:50%;transform:translateX(-50%);width:600px;height:600px;background:radial-gradient(circle,rgba(42,157,143,0.08) 0%,transparent 70%);pointer-events:none}
  .t-hero-i{max-width:1320px;margin:0 auto;display:flex;align-items:flex-start;justify-content:space-between;gap:40px;flex-wrap:wrap;position:relative}
  .t-hero-lbl{display:inline-flex;align-items:center;gap:8px;padding:5px 14px;border-radius:20px;background:${T.softTeal};border:1px solid ${T.border};color:${T.primary};font-size:11px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;margin-bottom:20px}
  .t-hero-h{font-family:'Playfair Display',serif;font-size:clamp(1.8rem,4.5vw,3.4rem);font-weight:900;color:${T.navy};letter-spacing:-.03em;line-height:1.06;margin-bottom:14px}
  .t-hero-h em{font-style:normal;color:${T.accentGold}}
  .t-hero-p{font-size:clamp(.85rem,1.3vw,1rem);color:${T.textMid};line-height:1.7;max-width:500px}
  .t-stats{display:flex;gap:1px;background:${T.border};border-radius:14px;border:1px solid ${T.border};overflow:hidden;align-self:flex-start;flex-shrink:0}
  .t-st{background:${T.white};padding:20px 28px;display:flex;flex-direction:column;gap:4px;min-width:100px}
  .t-st:nth-child(2){background:${T.offWhite}}
  .t-st-n{font-family:'Playfair Display',serif;font-size:1.8rem;font-weight:900;color:${T.accentGold};line-height:1;letter-spacing:-.03em}
  .t-st-l{font-size:11px;color:${T.textMuted};font-weight:500;text-transform:uppercase;letter-spacing:.08em}

  .t-card{background:${T.white};border-radius:20px;border:1px solid ${T.borderLight};padding:28px;box-shadow:${T.shadow};transition:all 0.3s ease}
  .t-card:hover{transform:translateY(-6px);box-shadow:${T.shadowLg}}
  .t-event-card{background:${T.white};border-radius:20px;border:1px solid ${T.borderLight};margin-bottom:20px;overflow:hidden;box-shadow:${T.shadow};transition:all 0.3s ease;cursor:pointer}
  .t-event-card:hover{transform:translateY(-4px);box-shadow:${T.shadowMd};border-color:${T.primary}}
  .t-stat-card{background:${T.white};border-radius:20px;border:1px solid ${T.borderLight};padding:24px;text-align:center;transition:all 0.3s ease}
  .t-stat-card:hover{transform:translateY(-4px);box-shadow:${T.shadowMd}}
  .t-cta-w{padding:0 clamp(16px,4vw,64px) 48px;background:${T.offWhite}}
  .t-cta{max-width:1320px;margin:0 auto;background:${T.gradNavy};border:1px solid rgba(59,188,174,0.28);border-radius:20px;padding:clamp(24px,4vw,52px) clamp(24px,4vw,60px);display:flex;align-items:center;justify-content:space-between;gap:32px;flex-wrap:wrap;position:relative;overflow:hidden}
  .t-cta::before{content:'';position:absolute;inset:0;background:radial-gradient(circle at 50% 100%,rgba(212,160,23,0.12) 0%,transparent 60%);pointer-events:none}
  .t-cta-tag{display:inline-block;padding:4px 12px;border-radius:20px;background:rgba(212,160,23,0.15);border:1px solid rgba(212,160,23,0.25);color:${T.accentGoldLight};font-size:11px;letter-spacing:.08em;text-transform:uppercase;margin-bottom:12px;font-weight:700}
  .t-cta-h{font-family:'Playfair Display',serif;font-size:clamp(1.2rem,2.5vw,2rem);font-weight:900;color:${T.white};letter-spacing:-.025em;line-height:1.15}
  .t-cta-p{font-size:14px;color:${T.textMuted};margin-top:8px;line-height:1.6}
  .t-cta-r{display:flex;flex-direction:column;align-items:flex-end;gap:10px}
  .t-cta-btn{display:inline-flex;align-items:center;gap:8px;padding:13px 28px;background:${T.gradGold};color:${T.navyDeep};border-radius:50px;text-decoration:none;font-family:'Inter',sans-serif;font-size:14px;font-weight:700;transition:all .2s}
  .t-cta-btn:hover{background:${T.gradGoldReverse};transform:translateY(-2px);box-shadow:${T.shadowGold}}
  .t-footer{border-top:1px solid ${T.border};background:${T.white};padding:28px clamp(16px,4vw,64px);margin-top:40px}
  .t-fi{max-width:1320px;margin:0 auto;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:10px}
  .t-f-copy{font-size:12px;color:${T.textMuted};letter-spacing:.01em}

  /* ─── RESPONSIVE OVERRIDES ─── */
  @media (max-width: 1024px) {
    .t-hero-i { flex-direction: column; align-items: center; text-align: center; }
    .t-hero-p { max-width: 100%; }
    .t-stats { align-self: center; }
  }
  @media (max-width: 768px) {
    .t-hero { padding: 32px 16px 24px; }
    .t-stats { flex-direction: column; width: 100%; border-radius: 12px; }
    .t-st { padding: 16px; align-items: center; min-width: auto; width: 100%; }
    .t-st-n { font-size: 1.5rem; }
    .t-cta { flex-direction: column; text-align: center; padding: 28px 20px; }
    .t-cta-r { width: 100%; align-items: center; }
    .t-cta-btn { width: 100%; justify-content: center; white-space: normal; }
    .t-fi { flex-direction: column; text-align: center; gap: 12px; }
    .t-card { padding: 20px; }
    .events-grid { grid-template-columns: 1fr !important; }
    .events-month-pills { justify-content: center; }
    .event-card-actions { flex-direction: column; align-items: stretch; gap: 12px; }
    .event-card-actions .view-details { text-align: center; }
    .modal-content { padding: 24px 20px !important; }
    .modal-close { top: 12px !important; right: 12px !important; }
    .modal-title { font-size: 24px !important; }
  }
  @media (max-width: 480px) {
    .t-hero { padding: 24px 12px 20px; }
    .t-hero-h { font-size: 1.6rem; }
    .t-cta-h { font-size: 1.2rem; }
    .t-card { padding: 16px; }
    .t-st-n { font-size: 1.3rem; }
    .t-event-card { padding: 16px; }
    .t-event-card h3 { font-size: 18px; }
    .event-card-meta { flex-wrap: wrap; gap: 6px; }
    .month-pill { padding: 6px 14px; font-size: 12px; }
  }

  @keyframes spin{to{transform:rotate(360deg)}}
`;

const TYPECOLORS = {
  flagship: { color: T.primary, bg: 'rgba(42,157,143,0.12)', label: 'Flagship' },
  forum: { color: T.accentGold, bg: 'rgba(212,160,23,0.12)', label: 'Forum' },
  conference: { color: T.accent, bg: 'rgba(59,188,174,0.12)', label: 'Conference' },
  intensive: { color: T.success, bg: 'rgba(16,185,129,0.12)', label: 'Intensive' },
  summit: { color: T.primaryLight, bg: 'rgba(59,130,246,0.12)', label: 'Summit' },
  workshop: { color: T.accent, bg: 'rgba(59,188,174,0.12)', label: 'Workshop' },
  seminar: { color: T.primary, bg: 'rgba(42,157,143,0.12)', label: 'Seminar' },
  webinar: { color: T.accentGold, bg: 'rgba(212,160,23,0.12)', label: 'Webinar' },
  meetup: { color: T.success, bg: 'rgba(16,185,129,0.12)', label: 'Meetup' },
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const getMonthIdx = (dateStr) => {
  if (!dateStr) return -1;
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) return d.getMonth();
  const match = dateStr?.match(/(January|February|March|April|May|June|July|August|September|October|November|December)/i);
  return match ? MONTHS.findIndex(x => x.toLowerCase() === match[0].toLowerCase()) : -1;
};

function StatCard({ icon: Icon, value, label, grad, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      viewport={{ once: true }}
      className="t-stat-card"
    >
      <div style={{ width: 56, height: 56, background: grad, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
        <Icon size={26} color={T.white} />
      </div>
      <div style={{ fontSize: 32, fontWeight: 800, color: T.text, marginBottom: 6 }}>{value}</div>
      <div style={{ fontSize: 12, color: T.textMuted, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>
    </motion.div>
  );
}

function TypeBadge({ type }) {
  const cfg = TYPECOLORS[type?.toLowerCase()] || TYPECOLORS.conference;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase',
      color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.color}30`,
      padding: '4px 12px', borderRadius: 20,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: cfg.color }} />
      {cfg.label || type}
    </span>
  );
}

/* ─── EVENT DETAIL MODAL — responsive ────────────────────────────────────── */
function EventDetailModal({ event, onClose }) {
  const navigate = useNavigate();
  if (!event) return null;

  const eventSlug = event.slug || event.title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/--+/g, '-')
    .trim();

  const goRegister = () => navigate(`/event/${eventSlug}/register`);
  const goDetails = () => navigate(`/event/${eventSlug}`);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(13,43,62,0.65)', backdropFilter: 'blur(8px)',
        zIndex: 1000, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: 20,
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="modal-content"
        style={{
          maxWidth: 680, width: '100%', maxHeight: '85vh', overflowY: 'auto',
          background: T.white, borderRadius: 24, boxShadow: T.shadowLg, padding: 36,
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close (X) */}
        <button
          onClick={onClose}
          className="modal-close"
          style={{
            position: 'absolute', top: 20, right: 20,
            width: 32, height: 32, borderRadius: '50%',
            border: `1px solid ${T.borderLight}`, background: T.offWhite,
            color: T.textMid, cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}
        >
          <RiCloseLine size={18} />
        </button>

        {/* Badges */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap', paddingRight: 40 }}>
          <TypeBadge type={event.type} />
          {event.featured && (
            <span style={{ fontSize: 11, fontWeight: 700, color: T.accentGoldDark, background: 'rgba(212,160,23,0.12)', padding: '4px 12px', borderRadius: 20 }}>
              ⭐ Featured
            </span>
          )}
          <span style={{
            fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
            color: event.status === 'cancelled' ? T.danger : event.status === 'completed' ? T.success : T.textMuted,
          }}>
            {event.status}
          </span>
        </div>

        {/* Title / subtitle */}
        <h2 className="modal-title" style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 800, color: T.text, marginBottom: 6, lineHeight: 1.25 }}>
          {event.title}
        </h2>
        {event.subtitle && (
          <p style={{ fontSize: 15, color: T.primary, fontWeight: 600, marginBottom: 22 }}>
            {event.subtitle}
          </p>
        )}

        {/* Key facts grid */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 16, marginBottom: 24, padding: 18, background: T.offWhite, borderRadius: 14,
        }}>
          <div>
            <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Date</div>
            <div style={{ fontSize: 14, color: T.text, fontWeight: 600 }}>{event.date || '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Time</div>
            <div style={{ fontSize: 14, color: T.text, fontWeight: 600 }}>
              {event.startTime || event.endTime
                ? `${event.startTime || '—'}${event.endTime ? ` – ${event.endTime}` : ''}`
                : '—'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Location</div>
            <div style={{ fontSize: 14, color: T.text, fontWeight: 600 }}>{event.location || '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Venue</div>
            <div style={{ fontSize: 14, color: T.text, fontWeight: 600 }}>{event.venue || '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Price</div>
            <div style={{ fontSize: 14, color: T.text, fontWeight: 600 }}>{event.price || 'Free'}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Capacity</div>
            <div style={{ fontSize: 14, color: T.text, fontWeight: 600 }}>{event.capacity || '—'}</div>
          </div>
        </div>

        {/* Description */}
        {event.description && (
          <div style={{ marginBottom: 22 }}>
            <h4 style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              About
            </h4>
            <p style={{ fontSize: 14, color: T.textMid, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
              {event.description}
            </p>
          </div>
        )}

        {/* Speakers */}
        {event.speakers?.length > 0 && (
          <div style={{ marginBottom: 22 }}>
            <h4 style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Speakers ({event.speakers.length})
            </h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {event.speakers.map((s, idx) => (
                <span key={idx} style={{ background: T.softTeal, color: T.primaryDark, padding: '5px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
                  🎤 {s}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Tracks */}
        {event.tracks?.length > 0 && (
          <div style={{ marginBottom: 22 }}>
            <h4 style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Tracks / Topics ({event.tracks.length})
            </h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {event.tracks.map((t, idx) => (
                <span key={idx} style={{ background: T.white, border: `1px solid ${T.border}`, color: T.textMid, padding: '5px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
                  📌 {t}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 12, paddingTop: 18, borderTop: `1px solid ${T.borderLight}`, flexWrap: 'wrap' }}>
          <button
            onClick={goRegister}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 24px', background: T.gradGold, color: T.navyDeep,
              border: 'none', borderRadius: 30, fontSize: 14, fontWeight: 700, cursor: 'pointer',
              boxShadow: T.shadowGold,
            }}
          >
            Register Now <RiArrowRightLine size={14} />
          </button>
          <button
            onClick={goDetails}
            style={{
              padding: '10px 24px', borderRadius: 30, border: `1px solid ${T.border}`,
              background: T.white, color: T.primary, cursor: 'pointer', fontSize: 14, fontWeight: 600,
            }}
          >
            Open Full Page
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '10px 24px', borderRadius: 30, border: `1px solid ${T.border}`,
              background: T.white, color: T.textMid, cursor: 'pointer', fontSize: 14, fontWeight: 600,
              marginLeft: 'auto',
            }}
          >
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function EventCard({ event, onClick }) {
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();

  const eventSlug = event.slug || event.title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/--+/g, '-')
    .trim();

  const handleViewDetails = (e) => {
    e.stopPropagation();
    if (!eventSlug) {
      toast.error('Event slug not available');
      return;
    }
    navigate(`/event/${eventSlug}`);
  };

  const handleRegister = (e) => {
    e.stopPropagation();
    if (!eventSlug) {
      toast.error('Event slug not available');
      return;
    }
    navigate(`/event/${eventSlug}/register`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="t-event-card"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onClick(event)}
      style={{ cursor: 'pointer' }}
    >
      <div style={{ padding: '24px 28px' }}>
        <div className="event-card-meta" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <TypeBadge type={event.type} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <RiCalendarEventLine size={12} style={{ color: T.textMuted }} />
            <span style={{ fontSize: 12, color: T.textMuted }}>{event.date}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <RiMapPinLine size={12} style={{ color: T.textMuted }} />
            <span style={{ fontSize: 12, color: T.textMuted }}>{event.location}</span>
          </div>
        </div>

        <h3 style={{ fontSize: 22, fontWeight: 800, color: T.text, marginBottom: 10, lineHeight: 1.3 }}>{event.title}</h3>
        <p style={{ fontSize: 14, color: T.textMid, marginBottom: 16, lineHeight: 1.6 }}>{event.subtitle || event.description}</p>

        <div className="event-card-actions" style={{ 
          marginTop: 20, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderTop: `1px solid ${T.borderLight}`,
          paddingTop: 16,
          flexWrap: 'wrap',
          gap: 12,
        }}>
          <div className="view-details" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            color: hovered ? T.primary : T.textMuted,
            fontSize: 13, fontWeight: 600,
            transition: 'all 0.2s',
            cursor: 'pointer'
          }} onClick={handleViewDetails}>
            View Details <RiArrowRightLine size={14} />
          </div>
          
          <button
            onClick={handleRegister}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '8px 20px',
              background: T.gradGold,
              color: T.navyDeep,
              border: 'none',
              borderRadius: 30,
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: T.shadowGold
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = T.shadowLg;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = T.shadowGold;
            }}
          >
            Register Now
            <RiArrowRightLine size={14} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function MonthPill({ label, count, active, onClick }) {
  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="month-pill"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '8px 20px', borderRadius: 40, fontSize: 13, fontWeight: 600,
        border: `1px solid ${active ? T.primary : T.borderLight}`,
        background: active ? T.gradPrimary : T.white,
        color: active ? T.white : T.textMid,
        cursor: 'pointer', transition: 'all 0.2s'
      }}
    >
      {label}
      <span style={{
        background: active ? 'rgba(255,255,255,0.25)' : T.softTeal,
        color: active ? T.white : T.primary,
        padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700
      }}>{count}</span>
    </motion.button>
  );
}

// ─── Helper function to fetch event and slides (already exported) ──────────
export const fetchEventAndSlides = async (slug) => {
  try {
    console.log(`🔍 1. Fetching event with slug: ${slug}`);
    
    const eventResponse = await axios.get(`${API_BASE}/api/events/slug/${slug}`);
    
    if (!eventResponse.data.success || !eventResponse.data.event) {
      return { success: false, error: 'Event not found', event: null, slides: [] };
    }
    
    const eventData = eventResponse.data.event;
    const eventId = eventData._id;
    
    console.log(`✅ 2. Found event: "${eventData.title}" with ID: ${eventId}`);
    
    const slidesResponse = await axios.get(
      `${API_BASE}/api/summit-hero-slides/active?eventId=${eventId}`
    );
    
    let slidesData = [];
    if (slidesResponse.data.success) {
      slidesData = slidesResponse.data.data || [];
    } else if (Array.isArray(slidesResponse.data)) {
      slidesData = slidesResponse.data;
    }
    
    const validSlides = slidesData.filter(
      slide => slide.title && slide.image && slide.isActive === true
    );
    
    console.log(`📊 4. Found ${validSlides.length} slides for event "${eventData.title}"`);
    
    return { success: true, event: eventData, slides: validSlides };
  } catch (error) {
    console.error('Error fetching event and slides:', error);
    return { success: false, error: error.message, event: null, slides: [] };
  }
};

export default function Events() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [showTop, setShowTop] = useState(false);
  const [email, setEmail] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null); // ← drives the modal

  useEffect(() => {
    fetchEvents();
    const onScroll = () => setShowTop(window.scrollY > 400);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE}/api/events`);
      if (data.success && data.events) {
        setEvents(data.events);
      } else {
        setEvents([]);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
      setEvents([]);
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  // ─── Card click now opens the modal instead of navigating away ──────────
  const handleEventClick = (clickedEvent) => {
    if (!clickedEvent) {
      toast.error("Event details not available");
      return;
    }
    setSelectedEvent(clickedEvent);
  };

  const byMonth = events.reduce((acc, e) => {
    const monthIdx = getMonthIdx(e.date);
    if (monthIdx !== -1) { 
      if (!acc[monthIdx]) acc[monthIdx] = [];
      acc[monthIdx].push(e); 
    }
    return acc;
  }, {});
  
  const activeMonths = Object.keys(byMonth).map(Number).sort((a, b) => a - b);
  const filtered = selectedMonth !== null ? events.filter(e => getMonthIdx(e.date) === selectedMonth) : events;
  const totalEvents = filtered.length;

  if (loading) {
    return (
      <div style={{ background: T.offWhite, minHeight: "100vh" }}>
        <style>{css}</style>
        <div className="t-hero">
          <div className="t-hero-i">
            <div>
              <div className="t-hero-lbl">
                <RiCalendarEventLine size={13} />
                Meetings21 · Events
              </div>
              <h1 className="t-hero-h">Discover <em>Events</em></h1>
              <p className="t-hero-p">Loading events...</p>
            </div>
          </div>
        </div>
        <div style={{ maxWidth: 1320, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '72px 24px', gap: 12 }}>
          <div style={{ width: 48, height: 48, border: `3px solid ${T.border}`, borderTop: `3px solid ${T.primary}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <p style={{ fontSize: 14, color: T.textMuted }}>Loading Events...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: T.offWhite, minHeight: "100vh" }}>
      <style>{css}</style>
      <Toaster position="top-right" toastOptions={{ style: { background: T.white, color: T.text, border: `1px solid ${T.borderLight}` } }} />

      {/* ══════════ HERO ══════════ */}
      <section className="t-hero">
        <div className="t-hero-i">
          <div>
            <div className="t-hero-lbl">
              <RiCalendarEventLine size={13} />
              Meetings21 · Events
            </div>
            <h1 className="t-hero-h">
              Discover <em>Events</em>
            </h1>
            <p className="t-hero-p">
              Join global leaders and innovators at premier events designed to transform industries and shape the future of your field.
            </p>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24 }}>
              <div style={{ flex: 1, height: 2, maxWidth: 80, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              <div style={{ width: 8, height: 8, background: T.accentGold, transform: 'rotate(45deg)' }} />
              <div style={{ flex: 1, height: 2, maxWidth: 80, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
            </div>
          </div>
          <div className="t-stats">
            <div className="t-st">
              <span className="t-st-n">{totalEvents}</span>
              <span className="t-st-l">Events</span>
            </div>
            <div className="t-st">
              <span className="t-st-n">2026</span>
              <span className="t-st-l">Year</span>
            </div>
            <div className="t-st">
              <span className="t-st-n">Global</span>
              <span className="t-st-l">Reach</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div style={{ maxWidth: 1320, margin: '0 auto', padding: '40px clamp(16px,4vw,64px) 80px' }}>

        {/* Month Explorer */}
        {activeMonths.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="t-card"
            style={{ padding: '20px 24px', marginBottom: 32 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: T.gradPrimary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <RiFolderChartLine size={18} color={T.white} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Browse by Month</div>
                <div style={{ fontSize: 11, color: T.textMuted }}>Filter events by time period</div>
              </div>
            </div>
            <div className="events-month-pills" style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <MonthPill label="All Events" count={events.length} active={selectedMonth === null} onClick={() => setSelectedMonth(null)} />
              {activeMonths.map(monthIdx => (
                <MonthPill key={monthIdx} label={MONTHS[monthIdx]} count={byMonth[monthIdx]?.length || 0} active={selectedMonth === monthIdx} onClick={() => setSelectedMonth(monthIdx)} />
              ))}
            </div>
          </motion.div>
        )}

        {/* Events List */}
        <AnimatePresence mode="wait">
          {filtered.length === 0 ? (
            <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="t-card" style={{ textAlign: 'center', padding: 'clamp(40px, 8vw, 60px) clamp(20px, 4vw, 40px)' }}>
              <RiCalendarEventLine size={48} style={{ color: T.textMuted, marginBottom: 16 }} />
              <h3 style={{ fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 8 }}>No Events Found</h3>
              <p style={{ color: T.textMuted }}>No events scheduled for this period. Try another month!</p>
            </motion.div>
          ) : (
            <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {filtered.map((event, idx) => (
                <EventCard key={event._id || idx} event={event} onClick={handleEventClick} />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Scroll to Top */}
      {showTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            position: 'fixed', bottom: 26, right: 26, zIndex: 200,
            width: 44, height: 44, borderRadius: 12, background: T.gradGold,
            border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center',
            justifyContent: 'center', color: T.navyDeep, boxShadow: T.shadowGold
          }}
        >
          <RiArrowUpLine size={20} />
        </button>
      )}

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedEvent && (
          <EventDetailModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
        )}
      </AnimatePresence>
      
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}